<template>
  <!--
    PoolaClaimSheet — the final money move (FE plan Section 4 + Section 5). Mirrors the
    DepositSheet.vue shell (:opened + update:opened, f7-preloader confirm,
    success screen, in-sheet error + toast). A CONTENT FRAGMENT — never its own
    <f7-page>.

    SERVER-CONFIRMED (D4): the confirm button spins on the POST; we patch stock +
    balances + show success ONLY on HTTP 200 — never optimistically. The claim is
    a final cash debit + an irreversible PoolShare burn.

    Five states (plan Section 4 sheet-states table):
      form              — mini summary + qty stepper + cost summary + burn notice
      confirming        — preloader + "don't close"; inputs locked
      success           — green check + post-claim balance + close
      insufficient-cash — form + inline error + top-up CTA (stays open)  [step==='form' && formError.code==='insufficient-cash']
      sold-out          — inline "sold out before you confirmed" + refresh-offers

    IDEMPOTENCY (plan Section 4): the sheet owns one attemptKey. It ROTATES on a new
    attempt (sheet open) and on any qty change (a different request). It REPLAYS
    verbatim on a network / timeout / 5xx retry with UNCHANGED inputs (the outcome
    is unknown — replaying is the only safe move; the server short-circuits a
    duplicate to the same purchase with zero side effects). A determinate 4xx that
    the user corrects by changing qty rotates via the qty-change path.

    ERROR MATRIX branches on errors[0].code ONLY (PoolaClaimError.code), never the
    human `detail` (plan Section 5).
  -->
  <f7-sheet
    class="poola-claim-sheet"
    :opened="opened"
    :close-by-backdrop-click="step !== 'confirming'"
    backdrop
    @sheet:closed="onClosed"
  >
    <f7-toolbar>
      <div class="left" />
      <div class="center">{{ t('poola.buy.claim.title') }}</div>
      <div class="right">
        <f7-link v-if="step !== 'confirming'" sheet-close>{{ t('general.close') }}</f7-link>
      </div>
    </f7-toolbar>

    <f7-page-content>
      <!-- SUCCESS + SOLD-OUT are terminal screens that do NOT read `offer` (it can
           flush to null on a refresh-offers reload) → kept OUTSIDE the offer guard
           so they never blank mid-render. -->
      <!-- A plain div, NOT <f7-block>: `.block` adds `margin: 35px 0` on the ios
           theme (framework7-bundle.css:2590, 2653), and margins sit OUTSIDE the box,
           so `min-height: 100%` would resolve to 100% + 70px — the block it is meant
           to centre would overflow and scroll. DepositSheet and WithdrawSheet use a
           plain div for the same reason. -->
      <div
        v-if="step === 'success'"
        class="text-align-center poola-claim-sheet__success sheet-fixed-footer-fill"
      >
        <f7-icon f7="checkmark_seal_fill" size="64" color="green" class="margin-bottom"></f7-icon>
        <h3 class="poola-claim-sheet__success-title">{{ t('poola.buy.claim.success') }}</h3>
        <p v-if="successBalanceLine" class="poola-claim-sheet__success-balance">
          {{ successBalanceLine }}
        </p>
      </div>

      <!-- ── SOLD OUT (canClaim:true → 409 race) ──────────────────── -->
      <div
        v-else-if="step === 'sold-out'"
        class="text-align-center poola-claim-sheet__soldout sheet-fixed-footer-fill"
      >
        <f7-icon f7="xmark_circle_fill" size="56" color="gray" class="margin-bottom"></f7-icon>
        <p class="poola-claim-sheet__soldout-msg">{{ t('poola.buy.error.offerSoldOut') }}</p>
      </div>

      <!-- ── FORM / CONFIRMING / INSUFFICIENT-CASH ────────────────── -->
      <template v-else-if="offer">
        <!-- mini offer summary: thumb + title + supplier + «بولشير %» seal -->
        <div class="poola-claim-sheet__summary">
          <div class="poola-claim-sheet__thumb">
            <img
              v-if="offer.photoUrl"
              :src="offer.photoUrl"
              :alt="offer.displayTitle"
              class="poola-claim-sheet__thumb-img"
              loading="lazy"
              @error="onThumbError"
            />
            <f7-icon
              v-else
              ios="f7:bag_fill"
              md="material:shopping_bag"
              size="22"
              color="white"
            ></f7-icon>
          </div>
          <div class="poola-claim-sheet__summary-body">
            <p class="poola-claim-sheet__summary-title">{{ offer.displayTitle }}</p>
            <p v-if="hasSupplier" class="poola-claim-sheet__summary-supplier">
              {{ offer.supplierName }}
            </p>
          </div>
          <span class="poola-claim-sheet__seal">{{
            t('poola.buy.card.shareBadge', { pct: discountPct })
          }}</span>
        </div>

        <!-- qty stepper + binding-constraint helper -->
        <div class="poola-claim-sheet__qty">
          <span class="poola-claim-sheet__qty-label">{{ t('poola.buy.claim.qty') }}</span>
          <f7-stepper
            :value="qty"
            :min="1"
            :max="stepperMax"
            :disabled="step === 'confirming' || maxQty < 1"
            small
            fill
            raised
            @stepper:change="onQtyChange"
          ></f7-stepper>
        </div>
        <p
          class="poola-claim-sheet__helper"
          :class="{ 'is-blocking': balanceUnavailable || maxQty < 1 }"
        >
          {{ helperText }}
        </p>

        <!-- cost summary: cash (hero) + PoolShare burn + split bar + total -->
        <div class="poola-claim-sheet__cost">
          <div class="poola-claim-sheet__cost-row poola-claim-sheet__cost-row--cash">
            <span>{{ t('poola.buy.claim.cashLine') }}</span>
            <span class="poola-claim-sheet__cost-val">{{ formatCurrency(totalCashCents) }}</span>
          </div>
          <div class="poola-claim-sheet__cost-row poola-claim-sheet__cost-row--ps">
            <span>{{ t('poola.buy.claim.poolshareLine') }}</span>
            <span class="poola-claim-sheet__cost-val">{{
              formatCurrency(totalPoolshareCents)
            }}</span>
          </div>
          <div
            v-if="runBudgetCents != null"
            class="poola-claim-sheet__cost-row poola-claim-sheet__cost-row--budget"
          >
            <span>{{ t('poola.buy.claim.runBudgetLine') }}</span>
            <span class="poola-claim-sheet__cost-val">{{
              formatCurrency(effectiveRunBudgetCents)
            }}</span>
          </div>

          <!-- dir="ltr" keeps cash-left / poolshare-right in all locales (mirrors the card) -->
          <div class="price-split__bar" dir="ltr">
            <div
              class="price-split__segment price-split__segment--cash"
              :style="{ flex: cashFlex }"
            />
            <div
              class="price-split__segment price-split__segment--poolshare"
              :style="{ flex: poolshareFlex }"
            />
          </div>

          <div class="poola-claim-sheet__cost-row poola-claim-sheet__cost-row--total">
            <span>{{ t('poola.buy.claim.totalLine') }}</span>
            <span class="poola-claim-sheet__cost-val">{{ formatCurrency(totalCents) }}</span>
          </div>
        </div>

        <!-- "your balance" affordability context -->
        <p class="poola-claim-sheet__balance">
          {{
            t('poola.buy.claim.balanceLine', {
              cash: cashBalanceDisplay,
              ps: poolshareBalanceDisplay,
            })
          }}
        </p>

        <!-- burn notice: the PoolShare spend is final + non-refundable (never softened) -->
        <p class="poola-claim-sheet__burn">
          <f7-icon
            f7="exclamationmark_triangle_fill"
            size="15"
            class="poola-claim-sheet__burn-icon"
          ></f7-icon>
          {{ t('poola.buy.claim.burnNotice') }}
        </p>

        <!-- inline error: insufficient-cash → top-up CTA; network → retry (the confirm flips to retry) -->
        <div v-if="formError" class="poola-claim-sheet__error">
          <p class="poola-claim-sheet__error-msg">{{ formErrorMessage }}</p>
          <f7-button
            v-if="formError.code === 'insufficient-cash'"
            outline
            class="poola-claim-sheet__topup"
            @click="goTopUp"
          >
            {{ t('poola.buy.claim.topUp') }}
          </f7-button>
        </div>
      </template>
    </f7-page-content>

    <!--
      ONE control drives every state, and it lives OUTSIDE the scroller.

      F7-Vue renders <f7-sheet> as [fixed children, #fixed slot, .sheet-modal-inner]
      (framework7-vue/components/sheet.js:206-212), so this div is a direct child of
      .sheet-modal — a sibling of the scroll container, not content inside it. Reaching
      it therefore does not depend on scrolling at all, which is the whole point: inside
      Telegram the sheet body's touch-scroll can be swallowed and an in-body CTA is
      simply unreachable. The z-index and padding rationale lives with the shared
      `.sheet-fixed-footer` rule in src/css/app.css.

      The v-if mirrors the body's own `v-else-if="offer"` guard. Each of the three
      buttons this replaced sat inside a block that already required an offer or a
      terminal step; an unguarded footer would offer «إعادة المحاولة» over an empty
      body if the bound offer flushed to null during an indeterminate window, since
      canConfirm returns true there before it ever looks at the offer.

      The `poola-claim-sheet__confirm` class is the e2e handle for the primary control
      (tests/e2e/poola-buy-screen.spec.js) and is kept across the state flip.
    -->
    <template #fixed>
      <div v-if="offer || step === 'success' || step === 'sold-out'" class="sheet-fixed-footer">
        <f7-button
          fill
          large
          class="poola-claim-sheet__confirm"
          :disabled="footerDisabled"
          @click="footerAction"
        >
          <f7-preloader
            v-if="step === 'confirming'"
            size="20"
            class="margin-end-half"
          ></f7-preloader>
          {{ footerLabel }}
        </f7-button>
        <!-- The shared footer rule uses min-height, so this second line grows the
             bar instead of being clipped by it. -->
        <p v-if="step === 'confirming'" class="poola-claim-sheet__dontclose">
          {{ t('poola.buy.claim.dontClose') }}
        </p>
      </div>
    </template>
  </f7-sheet>
</template>

<script setup>
import { f7 } from 'framework7-vue';
import { useRepo } from 'pinia-orm';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCurrency } from '@/composables/useCurrency.js';
import { usePoolaOffers } from '@/composables/usePoolaOffers.js';
import { useToast } from '@/composables/useToast.js';
import { POOLA_OFFER_STATUS } from '@/constants/poola.js';
import PoolaOffer from '@/models/commerce/PoolaOffer.js';
import { newIdempotencyKey } from '@/utils/idempotency.js';

const props = defineProps({
  // v-model:opened — drives the sheet open/closed from the run.
  opened: {
    type: Boolean,
    default: false,
  },
  // The PoolaOffer being claimed (null when the sheet is closed).
  offer: {
    type: Object,
    default: null,
  },
  // The current run — supplies the run-level «بولشير» budget (myPoolshareRemainingCents).
  deal: {
    type: Object,
    default: null,
  },
  // Live spendable balances { cashCents, poolshareCents } — bounds the stepper.
  // Either field may be null (wallet read failed / still in flight); the maxQty
  // math treats a null balance as 0 for a paid term (never NaN) and the helper
  // surfaces a "couldn't load your balance" affordance instead.
  balances: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits([
  'update:opened',
  // HTTP 200 → { purchase, balances } so the run swaps in the post-claim balance pill.
  'claimed',
  // 409 deal-not-active → the run asks the host to refetch the singleton (flip).
  'deal-not-active',
  // sold-out / over-cap → the run reloads the offers list (authoritative stock + myClaimedQty).
  'refresh-offers',
]);

const { t } = useI18n({ useScope: 'global' });
const { formatCurrency } = useCurrency();
const { showSuccess, showError } = useToast();

// The money move (server-confirmed; patches the shared PoolaOffer repo on 200).
// dealId is irrelevant to claim() — it keys off the offer id — but a sensible
// getter keeps the composable happy without triggering any list fetch.
const { claim } = usePoolaOffers(() => props.offer?.dealId ?? props.deal?.id ?? null);
// Direct repo handle for the local sold-out patch (same global pinia-orm store
// the run's list renders from — marking it here flips the card instantly).
const offerRepo = useRepo(PoolaOffer);

// ── State machine ────────────────────────────────────────────────────────────
const step = ref('form'); // 'form' | 'confirming' | 'success' | 'sold-out'
const qty = ref(1);
const formError = ref(null); // { code: 'insufficient-cash', needed, available } | { code: 'network' } | null
const successBalances = ref(null); // meta.balances from the winning claim

// Idempotency: one key per attempt; preserved across an indeterminate failure so
// a retry replays it verbatim.
const attemptKey = ref(null);
const lastFailureIndeterminate = ref(false);

// Deferred offers reload: set when a claim error needs the run to reload the
// authoritative list, fired from onClosed AFTER the sheet is gone — so the
// resulting repo flush can never blank the still-closing sheet.
const refreshOffersOnClose = ref(false);

// ── Offer-derived figures ────────────────────────────────────────────────────
const hasSupplier = computed(() => {
  const n = props.offer?.supplierName;
  return n != null && String(n).trim() !== '';
});
const discountPct = computed(() => Math.round((props.offer?.discountBp ?? 0) / 100));

const unitCashCents = computed(() => props.offer?.cashCents ?? 0);
const unitPoolshareCents = computed(() => props.offer?.poolshareCents ?? 0);
const unitPriceCents = computed(
  () => props.offer?.unitPriceCents ?? unitCashCents.value + unitPoolshareCents.value,
);

const totalCashCents = computed(() => unitCashCents.value * qty.value);
const totalPoolshareCents = computed(() => unitPoolshareCents.value * qty.value);
const totalCents = computed(() => unitPriceCents.value * qty.value);

// Per-unit split ratio (qty-invariant), mirrors the card's two-segment bar.
const splitTotal = computed(() => unitCashCents.value + unitPoolshareCents.value);
const cashFlex = computed(() =>
  splitTotal.value > 0 ? unitCashCents.value / splitTotal.value : 0.5,
);
const poolshareFlex = computed(() =>
  splitTotal.value > 0 ? unitPoolshareCents.value / splitTotal.value : 0.5,
);

const runBudgetCents = computed(() => props.deal?.myPoolshareRemainingCents ?? null);
// Displayed «PoolShare budget left» = min(run cap headroom, wallet balance).
// runBudgetCents is the backend's cap − used and ignores the wallet, so it can
// overstate the real spendable — this mirrors the maxQty clamp (byRun ∧ byPs)
// that already gates the purchase. Falls back to the raw headroom when the
// balance is unknown (wallet read in flight).
const effectiveRunBudgetCents = computed(() => {
  const remaining = runBudgetCents.value;
  if (remaining == null) return null;
  const balance = props.balances?.poolshareCents;
  return Number.isFinite(balance) ? Math.min(remaining, Math.max(0, balance)) : remaining;
});

// ── Balances ─────────────────────────────────────────────────────────────────
// Display floors at 0 — a negative available balance (e.g. a pending hold) reads
// as "nothing spendable", never a negative currency string.
const cashBalanceDisplay = computed(() =>
  props.balances?.cashCents == null ? '—' : formatCurrency(Math.max(0, props.balances.cashCents)),
);
const poolshareBalanceDisplay = computed(() =>
  props.balances?.poolshareCents == null
    ? '—'
    : formatCurrency(Math.max(0, props.balances.poolshareCents)),
);

// A paid term whose balance we couldn't read → we can't price affordability.
const balanceUnavailable = computed(() => {
  if (!props.offer) return false;
  return (
    (unitCashCents.value > 0 && props.balances?.cashCents == null) ||
    (unitPoolshareCents.value > 0 && props.balances?.poolshareCents == null)
  );
});

const successBalanceLine = computed(() => {
  const b = successBalances.value;
  if (!b) return '';
  const cash = b.defaultAvailableCents == null ? '—' : formatCurrency(b.defaultAvailableCents);
  const ps = b.poolshareCents == null ? '—' : formatCurrency(b.poolshareCents);
  return t('poola.buy.claim.balanceLine', { cash, ps });
});

// ── Affordable-max (D5) ──────────────────────────────────────────────────────
// Every constraint as a piece count; Infinity = "does not bind". A paid term
// (cash/poolshare) with a null balance binds to 0 (blocks) rather than NaN.
const constraints = computed(() => {
  const o = props.offer;
  if (!o) return null;
  const cash = unitCashCents.value;
  const ps = unitPoolshareCents.value;
  const availCash = props.balances?.cashCents;
  const availPs = props.balances?.poolshareCents;
  const runRemaining = runBudgetCents.value;

  const cap =
    o.maxPerMember == null ? Infinity : Math.max(0, o.maxPerMember - (o.myClaimedQty ?? 0));
  const stock = Math.max(0, o.piecesRemaining ?? 0);
  // cash === 0 / ps === 0 guards: a free term can't divide-by-zero or bind. A null
  // balance binds to 0 (blocks); a negative balance floors to 0 so a negative piece
  // count never leaks through Math.min and mislabels the binding reason.
  const byCash =
    cash === 0 ? Infinity : availCash == null ? 0 : Math.floor(Math.max(0, availCash) / cash);
  const byPs = ps === 0 ? Infinity : availPs == null ? 0 : Math.floor(Math.max(0, availPs) / ps);
  const byRun =
    runRemaining == null || ps === 0 ? Infinity : Math.floor(Math.max(0, runRemaining) / ps);
  return { cap, stock, byCash, byPs, byRun };
});

const maxQty = computed(() => {
  const c = constraints.value;
  if (!c) return 0;
  return Math.max(0, Math.min(c.stock, c.cap, c.byCash, c.byPs, c.byRun));
});

const stepperMax = computed(() => Math.max(1, maxQty.value));

// The binding constraint = the argmin (first-wins on ties, in cap→stock→cash→
// poolshare→runBudget priority) — names the reason in the helper.
const bindingReasonKey = computed(() => {
  const c = constraints.value;
  if (!c) return 'poola.buy.claim.reasonStock';
  const ordered = [
    ['poola.buy.claim.reasonCap', c.cap],
    ['poola.buy.claim.reasonStock', c.stock],
    ['poola.buy.claim.reasonCash', c.byCash],
    ['poola.buy.claim.reasonPoolshare', c.byPs],
    ['poola.buy.claim.reasonRunBudget', c.byRun],
  ];
  let bestKey = 'poola.buy.claim.reasonStock';
  let bestVal = Infinity;
  for (const [key, val] of ordered) {
    if (val < bestVal) {
      bestVal = val;
      bestKey = key;
    }
  }
  return bestKey;
});

const helperText = computed(() => {
  if (balanceUnavailable.value) return t('poola.buy.claim.balanceUnavailable');
  // Nothing claimable → name WHY (insufficient cash / sold out / cap), not "max reached".
  if (maxQty.value < 1) return t(bindingReasonKey.value);
  if (qty.value >= maxQty.value) return t('poola.buy.claim.maxReached');
  return `${t('poola.buy.claim.maxHelper', { n: maxQty.value })} · ${t(bindingReasonKey.value)}`;
});

// ── Confirm gating + label ───────────────────────────────────────────────────
const canConfirm = computed(() => {
  if (step.value !== 'form') return false;
  // Indeterminate-retry: REPLAY the already-sent request verbatim. The client qty
  // bound is irrelevant now (the server is the gate); blocking on a possibly-stale
  // bound would strand the user with an unresolved, possibly-committed attempt. The
  // frozen qty + preserved key guarantee the server dedups it to one purchase.
  if (lastFailureIndeterminate.value) return true;
  if (balanceUnavailable.value) return false;
  if (maxQty.value < 1) return false;
  if (qty.value < 1 || qty.value > maxQty.value) return false;
  // insufficient-cash race: block until the user reduces qty (clears it) or tops up.
  if (formError.value?.code === 'insufficient-cash') return false;
  return true;
});

const confirmLabel = computed(() => {
  if (step.value === 'confirming') return t('poola.buy.claim.confirming');
  if (lastFailureIndeterminate.value) return t('general.retry');
  return t('poola.buy.claim.confirm');
});

// ── Sticky footer: one control, every state ──────────────────────────────────
/*
  The footer is ONE persistent node in ONE fixed position, so a tap meant for the
  state it just left can land on the state it just entered. That is a real defect
  class, not a theoretical one: on the deposit sheet the same collapse let three
  taps on one pixel POST a request the member never confirmed (bb6c4ee). Here the
  pixel that meant «أكّد الحجز» — a final cash debit plus an irreversible
  PoolShare burn — becomes «إغلاق» the instant the POST resolves, and «تحديث
  العروض» the instant a 409 lands. A tap still travelling when that flip happens
  would dismiss a confirmation the member has not read, or discard the sold-out
  explanation for why their money was NOT taken.

  So the two TERMINAL steps latch for 400 ms. 'confirming' needs no latch —
  canConfirm is already false there, which is what stops a double-tap double
  charge. The indeterminate-retry replay is deliberately NOT latched: that button
  reads «إعادة المحاولة», the member is choosing to resolve an unknown outcome,
  and the preserved idempotency key makes the replay safe by design.
*/
const CONFIRM_SETTLE_MS = 400;
const LATCHED_STEPS = ['success', 'sold-out'];
const stepSettling = ref(false);
let stepSettleTimer = null;

watch(step, (next) => {
  clearTimeout(stepSettleTimer);
  stepSettling.value = LATCHED_STEPS.includes(next);
  if (!stepSettling.value) return;
  stepSettleTimer = setTimeout(() => {
    stepSettling.value = false;
  }, CONFIRM_SETTLE_MS);
});

onBeforeUnmount(() => clearTimeout(stepSettleTimer));

const footerLabel = computed(() => {
  if (step.value === 'success') return t('general.close');
  if (step.value === 'sold-out') return t('poola.buy.claim.refreshOffers');
  return confirmLabel.value;
});

const footerDisabled = computed(() => {
  if (step.value === 'success' || step.value === 'sold-out') return stepSettling.value;
  // `stepSettling` is already false here — the pre-flush watcher clears it on every
  // transition OUT of a latched step, before any render or click handler runs. It is
  // repeated anyway so the guard does not depend on watcher flush ORDER holding on a
  // path that spends real money.
  return !canConfirm.value || stepSettling.value;
});

function footerAction() {
  // Re-guarded, and this is NOT belt-and-braces: framework7-vue renders
  // <f7-button disabled> as `<a class="button … disabled">` (components/button.js:171)
  // — `disabled` is a CLASS, and the only thing it does is
  // `pointer-events: none !important` (framework7-bundle.css:247-250). That blocks
  // hit-testing, not a keyboard Enter on a focused <a href="#">, assistive-tech
  // activation, or a future `type="button"` refactor. On an irreversible cash debit
  // plus a PoolShare burn, the handler owes its own check.
  if (footerDisabled.value) return;
  if (step.value === 'success') {
    close();
    return;
  }
  if (step.value === 'sold-out') {
    onRefreshAndClose();
    return;
  }
  // 'form' and 'confirming' — onConfirm re-checks canConfirm itself.
  onConfirm();
}

const formErrorMessage = computed(() => {
  const e = formError.value;
  if (!e) return '';
  if (e.code === 'insufficient-cash') {
    return t('poola.buy.error.insufficientCash', { needed: e.needed, available: e.available });
  }
  if (e.code === 'network') return t('poola.buy.error.network');
  return t('poola.buy.error.generic');
});

// ── Qty mutation (the single rotation point) ─────────────────────────────────
// Any qty change is a DIFFERENT request → rotate the idempotency key and clear a
// stale indeterminate flag + correctable inline error.
function setQty(value) {
  const n = Number(value);
  // Clamp into [1, maxQty]: the F7 stepper wrapper doesn't re-apply `max` to the
  // live instance on a reactive change, so its internal max can go stale and emit a
  // value above the affordable max — never let that past the bound.
  const ceiling = Math.max(1, maxQty.value);
  const next = Number.isInteger(n) && n >= 1 ? Math.min(n, ceiling) : 1;
  if (next === qty.value) return;
  qty.value = next;
  attemptKey.value = newIdempotencyKey();
  lastFailureIndeterminate.value = false;
  formError.value = null;
}

function onQtyChange(value) {
  setQty(value);
}

function onThumbError(event) {
  event.target.style.display = 'none';
}

// Clamp down if the affordable-max drops below the chosen qty (e.g. a balance
// refresh lowered it). setQty rotates the key — the effective request changed.
watch(maxQty, (m) => {
  // NEVER auto-clamp during an indeterminate-retry window: setQty() rotates the
  // idempotency key, abandoning the unresolved (possibly committed) attempt and
  // risking a double charge. Freeze qty + key until the user retries (verbatim
  // replay) or explicitly changes qty (their own choice to start a new attempt).
  if (lastFailureIndeterminate.value) return;
  if (m >= 1 && qty.value > m) setQty(m);
});

// Fresh attempt whenever the sheet (re)opens OR the bound offer identity changes
// to a DIFFERENT valid offer while open — either is a brand-new claim that must
// mint a fresh idempotency key + reset state, so a new offer can never be claimed
// under a prior offer's key. The watch key is the offer id only while open with a
// valid offer; null when closed OR when offer flushes to null (a teardown, NOT a
// new claim) — so a null offer never triggers a reset that would flip a terminal
// success/sold-out screen back to the form. immediate so a sheet mounted
// already-open also initialises (in production it mounts closed → no-op until the
// first real open).
watch(
  () => (props.opened && props.offer?.id ? props.offer.id : null),
  (key) => {
    if (key == null) return;
    step.value = 'form';
    qty.value = 1;
    formError.value = null;
    lastFailureIndeterminate.value = false;
    successBalances.value = null;
    refreshOffersOnClose.value = false;
    attemptKey.value = newIdempotencyKey();
  },
  { immediate: true },
);

// ── Confirm → claim (server-confirmed) ───────────────────────────────────────
async function onConfirm() {
  if (!canConfirm.value) return;
  formError.value = null;
  step.value = 'confirming';
  // attemptKey is already correct: rotated on open / qty-change, PRESERVED across
  // an indeterminate failure → this reuse IS the verbatim replay.
  const key = attemptKey.value;
  try {
    const result = await claim(props.offer, qty.value, key);
    // HTTP 200 only — claim() already patched the offer in the shared repo.
    lastFailureIndeterminate.value = false;
    successBalances.value = result?.balances ?? null;
    emit('claimed', result); // run swaps in post-claim balances (does NOT close — sheet owns success)
    step.value = 'success';
    showSuccess(t('poola.buy.claim.success'));
  } catch (err) {
    handleClaimError(err);
  }
}

function handleClaimError(err) {
  const code = err?.code ?? null;
  const status = err?.status ?? null;

  // No determinate code from the server:
  if (code === null) {
    if (status === 401) {
      // Session expired mid-claim. The global axios interceptor (auth:unauthorized)
      // owns logout + redirect to /login/ — don't offer a doomed "retry"; just close.
      step.value = 'form';
      close();
      return;
    }
    // Genuine network / timeout / 5xx → outcome UNKNOWN → a retry replays the key.
    lastFailureIndeterminate.value = true;
    formError.value = { code: 'network' };
    step.value = 'form';
    showError(t('poola.buy.error.network'));
    return;
  }

  // DETERMINATE failure: the server created NO purchase, so this attempt is settled
  // → rotate the key now (plan Section 4: every determinate failure rotates; only an
  // indeterminate outcome replays). A same-qty retry then sends a fresh attempt.
  // Default back to a clean form (clears the in-flight 'confirming' for the
  // close-and-bubble cases); sold-out / insufficient-cash override below.
  lastFailureIndeterminate.value = false;
  attemptKey.value = newIdempotencyKey();
  formError.value = null;
  step.value = 'form';

  switch (code) {
    case 'not-eligible':
      // Shouldn't happen for an activated member — toast + close.
      showError(t('poola.buy.error.notEligible'));
      close();
      break;
    case 'deal-not-active':
      // The run flipped under us → host refetches the singleton; run closes the sheet.
      showError(t('poola.buy.error.dealNotActive'));
      emit('deal-not-active');
      break;
    case 'offer-not-published':
      // Deal active but THIS offer isn't live yet (scheduled-preview race / stale
      // offer). The backend groups it with deal-not-active in the same 409 branch.
      // Reload the list AND flip the singleton in case the run itself changed.
      showError(t('poola.buy.error.dealNotActive'));
      refreshOffersOnClose.value = true;
      emit('deal-not-active');
      break;
    case 'offer-sold-out':
      // Lost the race for the last piece(s). Mark locally (the card flips instantly);
      // the authoritative reload fires from onClosed on ANY close path.
      markOfferSoldOut();
      formError.value = null;
      refreshOffersOnClose.value = true;
      step.value = 'sold-out';
      showError(t('poola.buy.error.offerSoldOut'));
      break;
    case 'over-piece-cap':
      // Hit the per-offer cap. Reload the list (authoritative myClaimedQty) and close
      // — reopening yields a fresh offer snapshot, not a stale open stepper.
      showError(t('poola.buy.error.overCap'));
      refreshOffersOnClose.value = true;
      close();
      break;
    case 'deal-poolshare-cap-exceeded':
      formError.value = null;
      step.value = 'form';
      showError(t('poola.buy.error.dealPoolshareCap'));
      break;
    case 'insufficient-cash': {
      // Race (canClaim:true → 422): client thought it affordable. Inline + top-up;
      // stay open. Confirm stays disabled until qty drops or the wallet is topped up.
      const needed = formatCurrency(totalCashCents.value);
      const available = formatCurrency(Math.max(0, props.balances?.cashCents ?? 0));
      formError.value = { code: 'insufficient-cash', needed, available };
      step.value = 'form';
      showError(t('poola.buy.error.insufficientCash', { needed, available }));
      break;
    }
    case 'insufficient-poolshare':
      formError.value = null;
      step.value = 'form';
      showError(t('poola.buy.error.insufficientPoolshare'));
      break;
    default:
      // Laravel validation / unknown 4xx (qty is bounded → shouldn't reach here).
      formError.value = null;
      step.value = 'form';
      showError(t('poola.buy.error.generic'));
  }
}

// Flip the offer to sold-out in the shared repo (the run's list re-renders from it).
// Guard on existence — mirror usePoolaOffers.claim()'s `if (current)` so a flushed/
// evicted row is never resurrected as a partial ghost. Bump piecesClaimed to total
// so the card's progress bar reads 100% (consistent with the sold-out badge) until
// the authoritative reload lands.
function markOfferSoldOut() {
  const id = props.offer?.id;
  if (id == null) return;
  const current = offerRepo.find(id);
  if (!current) return;
  offerRepo.save({
    id,
    piecesRemaining: 0,
    piecesClaimed: current.piecesTotal ?? current.piecesClaimed ?? 0,
    canClaim: false,
    status: POOLA_OFFER_STATUS.SOLD_OUT,
  });
}

function goTopUp() {
  close();
  f7?.views?.main?.router?.navigate('/wallet/?autoOpenDeposit=1');
}

function onRefreshAndClose() {
  // refreshOffersOnClose is already set (sold-out path) → onClosed fires the reload
  // exactly once, AFTER the sheet is gone, on every close path (button/swipe/backdrop).
  close();
}

function close() {
  // Guarded so a programmatic close() + the subsequent sheet:closed don't both emit.
  if (props.opened) emit('update:opened', false);
}

function onClosed() {
  // Fire any deferred authoritative reload now that the sheet is gone (the repo
  // flush can't blank a closed sheet). Covers swipe/backdrop closes too, not just
  // the explicit buttons.
  if (refreshOffersOnClose.value) {
    refreshOffersOnClose.value = false;
    emit('refresh-offers');
  }
  if (props.opened) emit('update:opened', false);
}
</script>

<style scoped>
/*
  DEFINITE height, deliberately — not `height: auto; max-height: 92%`.

  With a definite height Framework7's own chain does all the work and the body
  needs no `sheet-scroll` flex scaffolding:
    .sheet-modal        height: 92% of the app root;  overflow: hidden
    .sheet-modal-inner  height: 100%;                 overflow: hidden
    .page-content       height: 100%;                 overflow: auto
  An auto height collapses both of those percentages back to `auto`, which is why
  auto-height sheets need that utility. This one has no auto height to rescue.

  The unit is a PERCENTAGE of the app root, never vh/dvh: inside Telegram
  `html.telegram-webapp` sizes that root to the visible band while vh measures the
  whole WebView. tests/unit/styles/sheet-scroll-invariant.test.js enforces it.

  92%, matching the wallet sheets: the body carries a summary, a stepper, four
  cost lines, a balance line, a burn notice and an error region, and the sheet no
  longer hugs its content between states.
*/
.poola-claim-sheet {
  height: 92%;

  /* Framework7's .page-content folds this straight into its own padding-bottom
     (framework7-bundle.css:391), so the burn notice and the error region clear the
     sticky footer instead of hiding under it. Keep it equal to the shared rule's
     fallback in src/css/app.css — the guard test pins the two together. */
  --sheet-fixed-footer-h: 76px;
  --f7-page-content-extra-padding-bottom: var(--sheet-fixed-footer-h);
}

.poola-claim-sheet .center {
  font-weight: var(--poolit-font-weight-semibold, 600);
}

/* ── Mini offer summary ──────────────────────────────────────────────────── */
.poola-claim-sheet__summary {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 12px 16px 4px;
}

.poola-claim-sheet__thumb {
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  border-radius: var(--poolit-radius-md, 10px);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    var(--poolit-primary-700, #6d28d9),
    var(--poolit-primary-900, #3b0764)
  );
}

.poola-claim-sheet__thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.poola-claim-sheet__summary-body {
  flex: 1;
  min-width: 0;
}

.poola-claim-sheet__summary-title {
  font-size: var(--poolit-font-size-body, 0.9375rem);
  font-weight: var(--poolit-font-weight-bold, 700);
  margin: 0;
  line-height: 1.3;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.poola-claim-sheet__summary-supplier {
  font-size: var(--poolit-font-size-small, 0.8125rem);
  color: var(--poolit-text-secondary, #6b7280);
  margin: 2px 0 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.poola-claim-sheet__seal {
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: var(--poolit-radius-full, 999px);
  background: var(--poolit-primary-600, #7c3aed);
  color: #fff;
  font-size: 12px;
  font-weight: var(--poolit-font-weight-bold, 700);
  white-space: nowrap;
}

/* ── Qty ─────────────────────────────────────────────────────────────────── */
.poola-claim-sheet__qty {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 16px 16px 4px;
}

.poola-claim-sheet__qty-label {
  font-size: var(--poolit-font-size-body, 0.9375rem);
  font-weight: var(--poolit-font-weight-semibold, 600);
}

.poola-claim-sheet__helper {
  margin: 0 16px;
  font-size: var(--poolit-font-size-small, 0.8125rem);
  color: var(--poolit-text-secondary, #6b7280);
}

.poola-claim-sheet__helper.is-blocking {
  color: var(--poolit-danger-600, #dc2626);
}

/* ── Cost summary ────────────────────────────────────────────────────────── */
.poola-claim-sheet__cost {
  margin: 16px 16px 0;
  padding: 14px 16px;
  border-radius: var(--poolit-radius-lg, 14px);
  background: var(--poolit-bg-secondary, rgba(0, 0, 0, 0.03));
}

.poola-claim-sheet__cost-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  font-size: var(--poolit-font-size-small, 0.8125rem);
  color: var(--poolit-text-secondary, #6b7280);
  margin-bottom: 6px;
}

.poola-claim-sheet__cost-val {
  font-variant-numeric: tabular-nums;
  font-weight: var(--poolit-font-weight-semibold, 600);
  color: var(--poolit-text-primary, #111827);
}

/* Cash is the hero figure (D2 cash-hero). */
.poola-claim-sheet__cost-row--cash {
  font-size: var(--poolit-font-size-body, 0.9375rem);
}

.poola-claim-sheet__cost-row--cash .poola-claim-sheet__cost-val {
  font-size: var(--poolit-font-size-lg, 1.125rem);
  font-weight: var(--poolit-font-weight-bold, 700);
}

.poola-claim-sheet__cost-row--ps .poola-claim-sheet__cost-val {
  color: var(--poolit-primary-700, #6d28d9);
}

.poola-claim-sheet__cost-row--total {
  margin-top: 10px;
  margin-bottom: 0;
  padding-top: 10px;
  border-top: 1px solid var(--poolit-border-color, rgba(0, 0, 0, 0.08));
  font-weight: var(--poolit-font-weight-semibold, 600);
  color: var(--poolit-text-primary, #111827);
}

/* split bar — mirrors PoolaOfferCard (dir="ltr" inline keeps cash-left). */
.price-split__bar {
  display: flex;
  height: 8px;
  border-radius: var(--poolit-radius-full, 999px);
  overflow: hidden;
  gap: 2px;
  margin: 10px 0;
}

.price-split__segment {
  border-radius: var(--poolit-radius-full, 999px);
  min-width: 4px;
}

.price-split__segment--cash {
  background: var(--poolit-neutral-500, #6b7280);
}

.price-split__segment--poolshare {
  background: var(--poolit-primary-500, #8b5cf6);
}

/* ── Balance + burn notice ───────────────────────────────────────────────── */
.poola-claim-sheet__balance {
  margin: 10px 16px 0;
  font-size: var(--poolit-font-size-small, 0.8125rem);
  color: var(--poolit-text-secondary, #6b7280);
  font-variant-numeric: tabular-nums;
}

.poola-claim-sheet__burn {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 10px 16px 0;
  font-size: var(--poolit-font-size-small, 0.8125rem);
  font-weight: var(--poolit-font-weight-medium, 500);
  color: var(--poolit-warning-700, #b45309);
}

.poola-claim-sheet__burn-icon {
  color: var(--poolit-warning-500, #f59e0b);
  flex-shrink: 0;
  margin-top: 1px;
}

/* ── Inline error + top-up ───────────────────────────────────────────────── */
.poola-claim-sheet__error {
  margin: 12px 16px 0;
  padding: 12px 14px;
  border-radius: var(--poolit-radius-md, 10px);
  background: var(--poolit-danger-50, rgba(220, 38, 38, 0.06));
  border: 1px solid var(--poolit-danger-200, rgba(220, 38, 38, 0.2));
}

.poola-claim-sheet__error-msg {
  margin: 0;
  font-size: var(--poolit-font-size-small, 0.8125rem);
  color: var(--poolit-danger-700, #b91c1c);
  line-height: 1.5;
}

.poola-claim-sheet__topup {
  margin-top: 10px;
}

/* ── Confirming / success / sold-out ─────────────────────────────────────── */
.poola-claim-sheet__dontclose {
  margin: 8px 0 0;
  text-align: center;
  font-size: var(--poolit-font-size-small, 0.8125rem);
  color: var(--poolit-text-muted, #6b7280);
}

.poola-claim-sheet__success,
.poola-claim-sheet__soldout {
  padding: 32px 24px;
}

.poola-claim-sheet__success-title {
  margin: 0 0 8px;
  font-size: var(--poolit-font-size-lg, 1.125rem);
  font-weight: var(--poolit-font-weight-bold, 700);
  line-height: 1.4;
}

.poola-claim-sheet__success-balance {
  margin: 0;
  color: var(--poolit-text-secondary, #6b7280);
  font-size: var(--poolit-font-size-small, 0.8125rem);
  font-variant-numeric: tabular-nums;
}

.poola-claim-sheet__soldout-msg {
  margin: 0;
  color: var(--poolit-text-secondary, #6b7280);
  font-size: var(--poolit-font-size-body, 0.9375rem);
  line-height: 1.5;
}
</style>
